import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

export default function Usage() {
  const { getToken } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/portal/usage`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setUsage(await res.json());
      } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div style={s.center}>Loading usage data...</div>;
  if (!usage)  return <div style={s.center}>Could not load usage data.</div>;

  const totalCost = (usage.total_cost_usd || 0).toFixed(4);

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.h1}>Usage</h1>
        <p style={s.subtitle}>Token consumption and cost breakdown for your account.</p>
      </div>

      {/* Summary stats */}
      <div style={s.statsRow}>
        {[
          { label: 'Sessions this month', value: usage.sessions_this_month || 0 },
          { label: 'Total tokens used',   value: (usage.total_tokens || 0).toLocaleString() },
          { label: 'Total cost (USD)',     value: `$${totalCost}` },
          { label: 'Quota remaining',      value: `${usage.quota_remaining ?? '-'}` },
        ].map(({ label, value }) => (
          <div key={label} style={s.statCard}>
            <div style={s.statValue}>{value}</div>
            <div style={s.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* By session */}
      {usage.by_session?.length > 0 && (
        <div className="scq-card" style={s.card}>
          <h2 style={s.cardTitle}>By session</h2>
          <table style={s.table}>
            <thead><tr>{['Session','Tokens','Cost (USD)','Date'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {usage.by_session.map((r, i) => (
                <tr key={i} style={s.tr}>
                  <td style={s.td}>{r.session_name || r.session_id?.slice(0,8)+'…'}</td>
                  <td style={s.td}>{(r.tokens||0).toLocaleString()}</td>
                  <td style={s.td}>${(r.cost_usd||0).toFixed(4)}</td>
                  <td style={s.td}>{r.date ? new Date(r.date).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* By action */}
      {usage.by_action?.length > 0 && (
        <div className="scq-card" style={s.card}>
          <h2 style={s.cardTitle}>By action</h2>
          <table style={s.table}>
            <thead><tr>{['Action','Calls','Tokens','Cost (USD)'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {usage.by_action.map((r, i) => (
                <tr key={i} style={s.tr}>
                  <td style={s.td}>
                    <span style={{...s.actionBadge, background: r.action?.includes('haiku') ? 'rgba(245,169,127,0.12)' : 'var(--color-accent-dim)', color: r.action?.includes('haiku') ? 'var(--color-amber)' : 'var(--color-accent)'}}>
                      {r.action}
                    </span>
                  </td>
                  <td style={s.td}>{r.calls}</td>
                  <td style={s.td}>{(r.tokens||0).toLocaleString()}</td>
                  <td style={s.td}>${(r.cost_usd||0).toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const s = {
  center: { color:'var(--color-muted)', textAlign:'center', padding:'80px 0', fontSize:14 },
  header: { marginBottom:32 },
  h1: { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, letterSpacing:'-0.02em', color:'var(--color-text)', margin:0 },
  subtitle: { color:'var(--color-muted)', marginTop:6, fontSize:14 },
  statsRow: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 },
  statCard: { background:'var(--color-card)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-lg)', padding:'20px 20px 16px', boxShadow:'var(--shadow-card)' },
  statValue: { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, letterSpacing:'-0.03em', color:'var(--color-text)', marginBottom:4 },
  statLabel: { fontSize:12, color:'var(--color-muted)' },
  card: { background:'var(--color-card)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-xl)', padding:24, marginBottom:20, boxShadow:'var(--shadow-card)' },
  cardTitle: { fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, color:'var(--color-text)', margin:'0 0 16px' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'8px 12px', fontSize:11, fontWeight:600, fontFamily:'var(--font-display)', color:'var(--color-muted2)', borderBottom:'1px solid var(--color-border)', textTransform:'uppercase', letterSpacing:'0.07em' },
  tr: { borderBottom:'1px solid var(--color-border)' },
  td: { padding:'13px 12px', fontSize:14, color:'var(--color-text)' },
  actionBadge: { padding:'3px 9px', borderRadius:100, fontSize:11, fontWeight:600, fontFamily:'monospace' },
};
